import React from "react";
import { Button, Popconfirm, Space, Tooltip } from "antd";
import {
    CloseOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    FilePdfOutlined,
} from "@ant-design/icons";

/**
 * Selection bar shown above a table once rows are ticked.
 *
 * Presentational only: the caller owns the selection state, the labels and the delete call, so
 * the same bar serves the Гүйлгээний төлөвлөгөө and Гүйлгээний Баримт tables without either
 * module depending on the other.
 *
 * It stays quiet while nothing is selected (it renders nothing at all) and the destructive
 * colour is carried by the delete button alone, so merely ticking a row never looks like an
 * alarm.
 */
function BulkDeleteBar({
    selectedCount = 0,
    attachmentCount = 0,
    loading = false,
    labels = {},
    onClear,
    onDelete,
}) {
    if (!selectedCount) return null;

    const {
        selected = "songogdson",
        attachments,
        clear = "Clear",
        delete: deleteLabel = "Delete",
        confirmTitle = "Delete the selected rows?",
        confirmBody,
        confirmOk = "Delete",
        confirmCancel = "Cancel",
    } = labels;

    return (
        <div
            className="bulk-bar mb-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2.5"
            role="region"
            aria-live="polite"
        >
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center rounded-full bg-blue-600 px-2.5 py-0.5 text-sm font-semibold text-white">
                    {selectedCount}
                </span>

                <span className="text-sm font-medium text-slate-800">{selected}</span>

                {attachmentCount > 0 && attachments && (
                    <Tooltip title={attachments}>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <FilePdfOutlined style={{ color: "#cf1322" }} />
                            {attachmentCount}
                        </span>
                    </Tooltip>
                )}
            </div>

            <Space wrap size={8}>
                <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={onClear}
                    disabled={loading}
                >
                    {clear}
                </Button>

                <Popconfirm
                    title={confirmTitle}
                    description={confirmBody}
                    okText={confirmOk}
                    cancelText={confirmCancel}
                    okButtonProps={{ danger: true, loading }}
                    icon={<ExclamationCircleFilled style={{ color: "#cf1322" }} />}
                    onConfirm={onDelete}
                >
                    <Button danger type="primary" size="small" icon={<DeleteOutlined />} loading={loading}>
                        {`${deleteLabel} (${selectedCount})`}
                    </Button>
                </Popconfirm>
            </Space>
        </div>
    );
}

export default BulkDeleteBar;
